import { LitElement, html } from 'lit';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { BreakpointBehavior } from '../fit-style/fit-breakpoint-behavior';
import styles from './fit-overlay.css';

const bodyScrollLockOptions = {
  reserveScrollBarGap: true,
};

class FitOverlay extends BreakpointBehavior(LitElement) {
  static get is() { return 'fit-overlay'; }

  static get styles() { return styles; }

  render() {
    return html`
      <div id="overlay" class="overlay-content" invisible>
        <div class="fixed">
          <slot name="selection-bar"></slot>
        </div>
        <div id="content" class="scrolling">
          <slot></slot>
        </div>
      </div>`;
  }

  static get properties() {
    return {

      opened: {
        type: Boolean,
        reflect: true,
      },

      embedded: {
        type: Boolean,
        reflect: true,
      },

      noScroll: {
        type: Boolean,
        attribute: 'no-scroll',
      },

      allowOutsideClick: {
        type: Boolean,
        attribute: 'allow-outside-click',
      },

      blockOutsideScroll: {
        type: Boolean,
        attribute: 'block-outside-scroll',
      },

      _isOnMobile: {
        type: Boolean,
      },
    };
  }

  constructor() {
    super();
    this.hidden = true;

    this.noScroll = false;
    this.allowOutsideClick = false;
    this.blockOutsideScroll = false;

    this._boundOnCaptureClick = this._onCaptureClick.bind(this);
    this._boundOnCaptureKeydown = this._onCaptureKeydown.bind(this);

    this.mql = window.matchMedia('max-width: 42.4375rem');
    this.mql.addListener(this._updateIsOnMobile);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('touchstart', this._stopPropagation);
    this.addEventListener('mousedown', this._stopPropagation);
  }

  /**
   * Toggle the opened state of the overlay.
   */
  toggle() {
    this.opened = !this.opened;
  }

  /**
   * Open the overlay.
   */
  open() {
    this.opened = true;
  }

  /**
   * Close the overlay.
   */
  close() {
    this.opened = false;
  }

  firstUpdated(changedProperties) {
    this.$ = {};
    this.$.overlay = this.shadowRoot.querySelector('#overlay');
    this.$.content = this.shadowRoot.querySelector('#content');
  }

  updated(changedProperties) {
    if (changedProperties.has('embedded')) {
      this._embeddedChanged();
    }
    if (changedProperties.has('opened')) {
      this._openedChanged();
    }
    if (changedProperties.has('noScroll')) {
      this._noScrollChanged(this.noScroll);
    }
  }

  get isVisible() {
    const rect = this.getBoundingClientRect();
    return (rect.height + rect.width) > 0;
  }

  _openedChanged() {
    if (this.opened) {
      this.removeAttribute('aria-hidden');
      this.hidden = false;
      let parentRect = null;
      if (this.parentNode) {
        if (this.parentNode instanceof Element) {
          parentRect = this.parentNode.getBoundingClientRect();
        } else if (this.parentNode instanceof ShadowRoot && this.parentNode.host) {
          parentRect = this.parentNode.host.getBoundingClientRect();
        }
      }
      window.requestAnimationFrame(() => {
        if (parentRect) {
          this._determineFit(parentRect);
        }
        this.$.overlay.removeAttribute('invisible');
      });
    } else {
      this.setAttribute('aria-hidden', 'true');
      this.hidden = true;
      this.$.overlay.setAttribute('invisible', '');
      this._unsetFit();
    }

    this._toggleListeners();
    this._toggleScrollLock();

    if (this.opened) {
      this.dispatchEvent(new CustomEvent('fit-overlay-opened', { bubbles: true, composed: true }));
    } else {
      this.dispatchEvent(new CustomEvent('fit-overlay-closed', { bubbles: true, composed: true }));
    }
  }

  _embeddedChanged() {
    this._toggleScrollLock();
  }

  _noScrollChanged(noScroll) {
    if (noScroll) {
      this.$.content.classList.remove('scrolling');
    } else {
      this.$.content.classList.add('scrolling');
    }
  }

  _determineFit(parentRect) {
    const bodyWidth = document.body.clientWidth;
    const rect = this.$.overlay.getBoundingClientRect();
    if (rect.right >= bodyWidth) {
      if ((parentRect.right - rect.width) <= 0) {
        this.classList.remove('right-fit');
        this.classList.add('middle-fit');
      } else {
        this.classList.remove('middle-fit');
        this.classList.add('right-fit');
      }
    } else {
      this._unsetFit();
    }
  }

  _unsetFit() {
    this.classList.remove('middle-fit');
    this.classList.remove('right-fit');
  }

  _toggleListener(enable, node, event, boundListener, capture) {
    if (enable) {
      node.addEventListener(event, boundListener, capture);
    } else {
      node.removeEventListener(event, boundListener, capture);
    }
  }

  _toggleListeners() {
    this._toggleListener(this.opened, document, 'click', this._boundOnCaptureClick, true);
    this._toggleListener(this.opened, document, 'keydown', this._boundOnCaptureKeydown, true);
  }

  _onCaptureClick(event) {
    if (!this.allowOutsideClick && this.opened && !event.composedPath().includes(this)) {
      this.close();
    }
  }

  _onCaptureKeydown(event) {
    const ESC = 27;
    if (!this.allowOutsideClick && this.opened && event.keyCode === ESC) {
      this.close();
    }
  }

  _toggleScrollLock() {
    if (!this.embedded && this.opened && (this.blockOutsideScroll || this._isOnMobile)) {
      disableBodyScroll(this.$.content, bodyScrollLockOptions);
    } else {
      enableBodyScroll(this.$.content);
    }
  }

  _updateIsOnMobile(event) {
    this._isOnMobile = event.matches;
  }

  _stopPropagation(event) {
    event.stopPropagation();
  }
}

customElements.define(FitOverlay.is, FitOverlay);
